import type { AddExternalAccess } from "@@/shared/acquire/access/commands/add-external-access-to-product";
import {
    type ResourceId,
    resourceIdZodSchema,
} from "@@/shared/acquire/resources/resource-id";
import {
    type IsoAndUnixTimestamp,
    isoAndUnixZodSchema,
    nowIsoAndUnix,
} from "@@/shared/date-time/iso-and-unix";
import {
    type TimeRange,
    timeRangeZodSchema,
} from "@@/shared/date-time/time-range";
import {
    type DurationInMinutes,
    durationInMinutesZodSchema,
} from "@@/shared/date-time/time-spans";
import {
    type ProviderId,
    providerIdZodSchema,
} from "@@/shared/providers/provider-id";
import type { Branded } from "@@/utilities/branded";
import { genericId } from "@@/utilities/generic-id";
import type { Except } from "type-fest";
import { z } from "zod";
import {
    type ProductId,
    productIdZodSchema,
} from "../../../products/product-id";

type AddExternalAccessToProviderId = Branded<
    string,
    "AddExternalAccessToProviderId"
>;
const {
    factory: addExternalAccessToProviderIdFactory,
    zodSchema: addExternalAccessToProviderIdZodSchema,
} = genericId<AddExternalAccessToProviderId, "block__">("block__");

type AddExternalAccessToProvider = {
    _id: string;
    _createdAt: IsoAndUnixTimestamp;
    _type: "ADD_EXTERNAL_ACCESS_COMMAND";
    providerId: ProviderId;

    name: string;
    valid?: TimeRange;
    validBefore: DurationInMinutes;
    validAfter: DurationInMinutes;
    requestedAccess: AddExternalAccess;

    productIds: ProductId[];
    resourceIds: ResourceId[];
};

type FactoryParams = Except<
    AddExternalAccessToProvider,
    "_id" | "_type" | "_createdAt"
>;
const addExternalAccessProviderFactory = (params: FactoryParams) => {
    const command: AddExternalAccessToProvider = {
        ...params,
        _id: addExternalAccessToProviderIdFactory(),
        _type: "ADD_EXTERNAL_ACCESS_COMMAND",
        _createdAt: nowIsoAndUnix(),
    };
    return command;
};

const addExternalAccessToProviderZodSchema: z.ZodSchema<AddExternalAccessToProvider> =
    z.object({
        _id: addExternalAccessToProviderIdZodSchema,
        _type: z.literal("ADD_EXTERNAL_ACCESS_COMMAND"),
        _createdAt: isoAndUnixZodSchema,

        providerId: providerIdZodSchema,
        name: z.string(),
        validBefore: durationInMinutesZodSchema,
        validAfter: durationInMinutesZodSchema,
        valid: timeRangeZodSchema.optional(),
        requestedAccess: z.union([
            z.object({ type: z.literal("PARAKEY"), keyId: z.string() }),
            z.object({ type: z.literal("PASSCODE"), code: z.string() }),
            z.object({
                type: z.literal("LOCKY"),
                roomId: z.string(),
            }),
        ]),
        productIds: z.array(productIdZodSchema),
        resourceIds: z.array(resourceIdZodSchema),
    });

export {
    addExternalAccessProviderFactory,
    addExternalAccessToProviderZodSchema,
};
export type { AddExternalAccessToProvider };
